import { useId, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { contextChips, type ChipId } from '@/services/feedbackContext/chips'
import { attachedLine } from '@/services/feedbackContext/feedbackContextText'
import type { FeedbackSnapshot } from '@/services/feedbackContext/environment'
import { cn } from '@/utils'
import {
  HIDE_ATTACHED, NOTHING_ATTACHED, NOTHING_ATTACHED_OPEN, SHOW_ATTACHED, previewChipLabel, removeChipLabel,
} from './helpFeedbackText'

/**
 * What goes with the report (decision 14): one line collapsed, the chips open, each showing exactly
 * what it carries and removable. A removal applies to the next turn and the send.
 */
export function AttachedRow({ snapshot, removed, onRemove, readOnly }: {
  snapshot: FeedbackSnapshot
  removed: ChipId[]
  onRemove: (chip: ChipId) => void
  readOnly?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState<ChipId | null>(null)
  const listId = useId()
  const chips = contextChips(snapshot).filter((c) => !removed.includes(c.id))
  const line = attachedLine(chips.map((c) => c.label)) || NOTHING_ATTACHED

  if (readOnly) return <p className="text-xs text-muted-foreground">{line}</p>

  return (
    <div className="text-xs">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={open ? HIDE_ATTACHED : SHOW_ATTACHED}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 rounded-sm text-left text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="min-w-0 flex-1 truncate">{line}</span>
        <ChevronDown aria-hidden className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div id={listId} className="mt-2 space-y-2">
          {chips.length === 0 ? (
            <p className="text-muted-foreground">{NOTHING_ATTACHED_OPEN}</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <li key={chip.id} className="flex items-center rounded-full border border-border bg-background">
                  <button
                    type="button"
                    aria-expanded={preview === chip.id}
                    aria-label={previewChipLabel(chip.label)}
                    onClick={() => setPreview((p) => (p === chip.id ? null : chip.id))}
                    className="rounded-l-full py-0.5 pl-2.5 pr-1 outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    {chip.label}
                  </button>
                  <button
                    type="button"
                    aria-label={removeChipLabel(chip.label)}
                    onClick={() => {
                      if (preview === chip.id) setPreview(null)
                      onRemove(chip.id)
                    }}
                    className="rounded-r-full py-0.5 pl-1 pr-2 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <X aria-hidden className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {preview && (
            <ul className="space-y-0.5 rounded-md bg-muted px-2.5 py-2 font-mono text-[11px] text-muted-foreground">
              {(chips.find((c) => c.id === preview)?.preview ?? []).map((l, i) => (
                <li key={i} className="break-words">{l}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
